import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  Badge,
  Button,
  confirm,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ExternalLinkIcon,
  PlusIcon,
  Trash2Icon,
  UsersIcon,
  WalletIcon,
  XIcon,
} from "lucide-react";
import type { AssignmentDTO, PayoutListItemDTO } from "../../domain/contracts";
import { performanceApi } from "../api/client";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";
import {
  useCancelPayout,
  useCreatePayout,
  useDeletePayout,
  useEventPayoutsQuery,
} from "../api/queries";
import { PayoutsSectionSkeleton } from "./EventDetailSectionSkeletons";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  paid: "outline",
  cancelled: "destructive",
} as const;

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

type PayoutsSectionProps = {
  eventId: string;
  assignments: AssignmentDTO[];
  onGoToTeam?: () => void;
};

type EventPayoutRowProps = {
  payout: PayoutListItemDTO;
  eventId: string;
  canCreate: boolean;
  canDelete: boolean;
  onOpen: () => void;
};

function EventPayoutRow({
  payout,
  eventId,
  canCreate,
  canDelete,
  onOpen,
}: EventPayoutRowProps) {
  const cancelPayout = useCancelPayout();
  const deletePayout = useDeletePayout(eventId);
  const isCancelled = payout.status === "cancelled";
  const canCancel =
    canCreate && !isCancelled && payout.status !== "paid";
  const canPrint =
    payout.status === "approved" || payout.status === "paid";
  const performerCount = payout.performerCount ?? (payout.isBatch ? 0 : 1);

  return (
    <div
      className={
        isCancelled
          ? "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-3"
          : "flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 px-4 py-3"
      }
    >
      <button
        type="button"
        className="min-w-0 text-left hover:underline disabled:pointer-events-none disabled:no-underline"
        disabled={isCancelled}
        onClick={onOpen}
      >
        <p className="font-semibold text-sm">{payout.payoutNo}</p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          {performerCount} ຄົນ · {formatMoney(payout.totalAmount)}
        </p>
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={STATUS_VARIANTS[payout.status] ?? "secondary"}>
          {STATUS_LABELS[payout.status] ?? payout.status}
        </Badge>
        {!isCancelled ? (
          <Button size="sm" variant="outline" className="h-8" onClick={onOpen}>
            <ExternalLinkIcon className="size-4" /> ເປີດ
          </Button>
        ) : null}
        {canPrint ? (
          <PerformanceBrandPrintMenu
            className="h-8 gap-1 px-2"
            buildPrintUrl={(lang) =>
              performanceApi.payouts.printUrl(payout.id, lang)
            }
          />
        ) : null}
        {canCancel ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-destructive hover:bg-destructive/10"
            disabled={cancelPayout.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "ຍົກເລີກໃບເບີກຈ່າຍ",
                description: "ໃບເບີກຈ່າຍນີ້ຈະຖືກປ່ຽນເປັນສະຖານະຍົກເລີກ",
                actionText: "ຍົກເລີກ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) {
                cancelPayout.mutate({
                  payoutId: payout.id,
                  reason: "ຍົກເລີກຈາກໜ້າລາຍລະອຽດງານ",
                });
              }
            }}
          >
            <XIcon className="size-4" /> ຍົກເລີກ
          </Button>
        ) : null}
        {isCancelled && canDelete ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-destructive hover:bg-destructive/10"
            disabled={deletePayout.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "ລຶບໃບເບີກຈ່າຍ",
                description:
                  "ໃບເບີກຈ່າຍທີ່ຍົກເລີກແລ້ວຈະຖືກລຶບຖາວອນ — ກູ້ຄືນບໍ່ໄດ້",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) deletePayout.mutate(payout.id);
            }}
          >
            <Trash2Icon className="size-4" /> ລຶບ
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function PayoutsSection({
  eventId,
  assignments,
  onGoToTeam,
}: PayoutsSectionProps) {
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  const canCreate = useActionPermission(["performance-payouts:create"]);
  const canDelete = useActionPermission(["performance-payouts:delete"]);
  const payoutsQuery = useEventPayoutsQuery(eventId);
  const createPayout = useCreatePayout(eventId);
  const payouts = payoutsQuery.data ?? [];

  const performerIds = new Set(assignments.map((a) => a.performerId));
  const hasAssignments = performerIds.size > 0;

  const performerBatchPayouts = payouts.filter(
    (item) => item.isBatch && !item.isStaffPayout && !item.isProductionPayout,
  );
  const activeBatchPayout = performerBatchPayouts.find(
    (item) => item.status !== "cancelled",
  );
  const cancelledBatchPayout = performerBatchPayouts.find(
    (item) => item.status === "cancelled",
  );
  const legacyPayouts = payouts.filter(
    (item) => !item.isBatch && item.status !== "cancelled",
  );

  const openPayout = (payoutId: string) =>
    nav({
      to: "/app/performance/payouts/$id",
      params: { id: payoutId },
    });

  if (payoutsQuery.isLoading) {
    return <PayoutsSectionSkeleton />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="flex items-center gap-2">
          <WalletIcon className="size-4 text-primary" />
          <span className="font-semibold text-sm uppercase tracking-widest">
            ໃບເບີກຈ່າຍນັກສະແດງ
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => nav({ to: "/app/performance/payouts" })}
        >
          <ExternalLinkIcon className="size-4" /> ເບິ່ງທັງໝົດ
        </Button>
      </div>

      <div className="flex-1 space-y-3 p-5">
        {legacyPayouts.length > 0 && !activeBatchPayout && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-900 text-sm dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
            <p>
              ມີໃບເບີກຈ່າຍແບບເກົ່າ (ຕໍ່ຄົນ) {legacyPayouts.length} ໃບຄ້າງຢູ່ —
              ຕ້ອງຍົກເລີກກ່ອນຈຶ່ງສ້າງໃບລວມໄດ້
            </p>
          </div>
        )}

        {!hasAssignments ? (
          <Empty className="py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <UsersIcon className="size-12 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>ຍັງບໍ່ມີນັກສະແດງ</EmptyTitle>
              <EmptyDescription>
                ຈັດທີມນັກສະແດງກ່ອນຈຶ່ງຈະສ້າງໃບເບີກຈ່າຍໄດ້
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onGoToTeam?.()}
              >
                ໄປຈັດທີມ
              </Button>
            </EmptyContent>
          </Empty>
        ) : activeBatchPayout ? (
          <EventPayoutRow
            payout={activeBatchPayout}
            eventId={eventId}
            canCreate={canCreate}
            canDelete={canDelete}
            onOpen={() => openPayout(activeBatchPayout.id)}
          />
        ) : (
          <>
            {cancelledBatchPayout ? (
              <>
                <EventPayoutRow
                  payout={cancelledBatchPayout}
                  eventId={eventId}
                  canCreate={canCreate}
                  canDelete={canDelete}
                  onOpen={() => openPayout(cancelledBatchPayout.id)}
                />
                {canCreate && legacyPayouts.length === 0 ? (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => createPayout.mutate()}
                      disabled={createPayout.isPending}
                    >
                      <PlusIcon className="size-4" />
                      {createPayout.isPending
                        ? "ກຳລັງສ້າງ..."
                        : "ສ້າງໃບເບີກຈ່າຍໃໝ່"}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <Empty className="py-8">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <WalletIcon className="size-12 text-muted-foreground" />
                  </EmptyMedia>
                  <EmptyTitle>ຍັງບໍ່ມີໃບເບີກຈ່າຍ</EmptyTitle>
                  <EmptyDescription>
                    {performerIds.size} ນັກສະແດງ — ສ້າງໃບລວມຄັ້ງດຽວເພື່ອຂໍອະນຸມັດ ແລະ
                    ຈ່າຍຄ່າຕອບແທນ
                  </EmptyDescription>
                </EmptyHeader>
                {canCreate && legacyPayouts.length === 0 ? (
                  <EmptyContent>
                    <Button
                      size="sm"
                      onClick={() => createPayout.mutate()}
                      disabled={createPayout.isPending}
                    >
                      <PlusIcon className="size-4" />
                      {createPayout.isPending
                        ? "ກຳລັງສ້າງ..."
                        : "ສ້າງໃບເບີກຈ່າຍ"}
                    </Button>
                  </EmptyContent>
                ) : !canCreate ? (
                  <EmptyContent>
                    <p className="text-muted-foreground text-xs">
                      ຕ້ອງມີສິດ performance-payouts:create
                    </p>
                  </EmptyContent>
                ) : null}
              </Empty>
            )}
          </>
        )}
      </div>
    </div>
  );
}
